// Vue carte de la liste des projets : chaque fiche à la position de sa ville (GeoNames) ; sans ville placée, au
// repère de son pays, distinct des points de ville (countryAnchors.ts).

import { useState } from "react";

export type ListView = "list" | "map";

// Un choix par écran (projets, affaires).
const viewKey = (screen: string) => `aegyra.${screen}.view`;

/** Vue choisie par l'utilisateur, gardée dans son navigateur ; la liste quand rien n'est lisible. */
export const readListView = (
  storage?: Pick<Storage, "getItem">,
  screen = "projects",
): ListView => {
  try {
    return (storage ?? window.localStorage).getItem(viewKey(screen)) === "map"
      ? "map"
      : "list";
  } catch {
    return "list";
  }
};

export const writeListView = (
  view: ListView,
  storage?: Pick<Storage, "setItem">,
  screen = "projects",
) => {
  try {
    (storage ?? window.localStorage).setItem(viewKey(screen), view);
  } catch {
    // Stockage refusé (navigation privée) : la vue vaut pour la page seulement.
  }
};

/** Vue liste ou carte d'un écran, retenue dans le navigateur de l'utilisateur. */
export const useListView = (screen: string) => {
  const [view, setView] = useState<ListView>(() =>
    readListView(undefined, screen),
  );
  return [
    view,
    (next: ListView) => {
      writeListView(next, undefined, screen);
      setView(next);
    },
  ] as const;
};

/** Cadre de la carte : pleine largeur sous l'en-tête sur téléphone, bordé sur ordinateur. */
export const mapFrameClass = (mobile?: boolean) =>
  mobile
    ? "relative -mx-4 h-[calc(100dvh-9.5rem)] overflow-hidden"
    : "relative h-[calc(100vh-10rem)] min-h-[420px] overflow-hidden rounded-[3px] border border-border";

/** Colonnes lues pour la carte : de quoi placer le point et remplir la carte de la fiche. */
export const MAP_COLUMNS = [
  "id",
  "name",
  "name_fr",
  "score",
  "city",
  "country",
  "phase",
  "category",
  "owner_names",
  "budget_amount",
  "budget_currency",
  "to_specify",
  "place_lat",
  "place_lon",
];

// Au-delà, la carte se limite aux fiches les mieux notées et le dit.
export const MAP_LIMIT = 5000;

// L'API rend au plus 1 000 lignes par requête (max_rows de PostgREST) : la carte lit page par page.
export const API_PAGE_SIZE = 1000;

/** Toutes les lignes d'une sélection, page par page, jusqu'au total ou à la limite. */
export const readAllPages = async <T>(
  getPage: (
    page: number,
    perPage: number,
  ) => Promise<{ data: T[]; total?: number }>,
  limit: number,
  pageSize = API_PAGE_SIZE,
) => {
  const data: T[] = [];
  let total: number | undefined;
  for (let page = 1; data.length < limit; page += 1) {
    const result = await getPage(page, pageSize);
    total = result.total;
    data.push(...result.data);
    if (!result.data.length || data.length >= (total ?? 0)) break;
  }
  return { data: data.slice(0, limit), total: total ?? data.length };
};

export type MapProject = {
  id: number | string;
  name: string;
  score?: number | null;
  place_lat?: number | null;
  place_lon?: number | null;
};

export type ProjectFeature = {
  type: "Feature";
  id: number;
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: { id: string; score: number; tier: ScoreTier };
};

export type ScoreTier = "high" | "mid" | "low";

/** Mêmes seuils que le reste de l'outil : 80 et plus, 60 à 79, le reste. */
export const scoreTier = (score?: number | null): ScoreTier =>
  score != null && score >= 80
    ? "high"
    : score != null && score >= 60
      ? "mid"
      : "low";

const placed = (value?: number | null): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** Points de la carte ; une fiche sans position n'en a pas. */
export const projectFeatures = (projects: MapProject[]) => {
  const features: ProjectFeature[] = [];
  projects.forEach((project, index) => {
    if (!placed(project.place_lat) || !placed(project.place_lon)) return;
    features.push({
      type: "Feature",
      // Identifiant numérique propre à la carte : MapLibre n'accepte pas les chaînes.
      id: index,
      geometry: {
        type: "Point",
        coordinates: [project.place_lon, project.place_lat],
      },
      properties: {
        id: String(project.id),
        score: project.score ?? 0,
        tier: scoreTier(project.score),
      },
    });
  });
  return { type: "FeatureCollection" as const, features };
};

/** Fiches de la liste filtrée qui ne sont pas sur la carte (sans ville, ou ville introuvable). */
export const unplacedCount = (listTotal?: number, placedTotal?: number) =>
  listTotal == null || placedTotal == null
    ? 0
    : Math.max(listTotal - placedTotal, 0);

/** Cadre qui contient tous les points, ou rien sans point. */
export const featuresBounds = (
  features: Pick<ProjectFeature, "geometry">[],
): [[number, number], [number, number]] | undefined => {
  if (!features.length) return undefined;
  let [west, south] = features[0].geometry.coordinates;
  let [east, north] = [west, south];
  for (const feature of features) {
    const [lon, lat] = feature.geometry.coordinates;
    west = Math.min(west, lon);
    east = Math.max(east, lon);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  return [
    [west, south],
    [east, north],
  ];
};

// Fond OpenFreeMap (données OpenStreetMap, ODbL ; tuiles libres sans clé), clair ou sombre selon le thème.
export const mapStyleUrl = (dark: boolean) =>
  `https://tiles.openfreemap.org/styles/${dark ? "dark" : "positron"}`;
